import { useMemo, useState } from 'react';
import { isValid, parseISO, startOfDay } from 'date-fns';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { CalendarClock, ImageIcon, ImagePlus, Type } from 'lucide-react';
import { ApiError, bffUpload } from '@/shared/api/client';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import { Switch } from '@/shared/ui/switch';
import { DatePicker } from '@/shared/ui/date-picker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Wizard, type WizardStep } from '@/shared/components/wizard';
import { toApiDateTime } from '../model/banner-image';
import { BannerImageCropper } from './banner-image-cropper';

const schema = z
  .object({
    title: z.string().min(1, 'Informe o título'),
    description: z.string().min(1, 'Informe a descrição'),
    url: z.string().url('URL inválida'),
    expiry: z.boolean(),
    entryDate: z.string().optional(),
    exitDate: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.expiry) return;
    if (!data.entryDate) {
      ctx.addIssue({ code: 'custom', message: 'Informe a data de entrada', path: ['entryDate'] });
    }
    if (!data.exitDate) {
      ctx.addIssue({ code: 'custom', message: 'Informe a data de saída', path: ['exitDate'] });
    }
    if (data.entryDate && data.exitDate && data.entryDate > data.exitDate) {
      ctx.addIssue({ code: 'custom', message: 'Saída deve ser após a entrada', path: ['exitDate'] });
    }
  });

type FormValues = z.infer<typeof schema>;

interface BannerCreateDialogProps {
  idPassbook: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

export function BannerCreateDialog({ idPassbook, open, onOpenChange, onCreated }: BannerCreateDialogProps) {
  const [sourceSrc, setSourceSrc] = useState<string | null>(null);
  const [croppedFile, setCroppedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { title: '', description: '', url: '', expiry: false, entryDate: '', exitDate: '' },
  });

  const expiry = form.watch('expiry');
  const errors = form.formState.errors;
  const today = useMemo(() => startOfDay(new Date()), []);

  const reset = () => {
    form.reset();
    if (sourceSrc?.startsWith('blob:')) URL.revokeObjectURL(sourceSrc);
    if (preview?.startsWith('blob:')) URL.revokeObjectURL(preview);
    setSourceSrc(null);
    setCroppedFile(null);
    setPreview(null);
  };

  const onValid = form.handleSubmit(async (values) => {
    if (!croppedFile) {
      toast.error('Recorte a imagem antes de continuar.');
      return;
    }
    const formData = new FormData();
    formData.append('passbookId', idPassbook);
    formData.append('title', values.title);
    formData.append('description', values.description);
    formData.append('url', values.url);
    formData.append('expiry', values.expiry ? 'S' : 'N');
    formData.append('active', 'true');
    if (values.expiry) {
      formData.append('entryDate', toApiDateTime(values.entryDate));
      formData.append('exitDate', toApiDateTime(values.exitDate));
    } else {
      formData.append('entryDate', '');
      formData.append('exitDate', '');
    }
    formData.append('banner', croppedFile);

    try {
      await bffUpload('/bff/uploads/banner', formData);
      toast.success('Banner adicionado.');
      reset();
      onOpenChange(false);
      onCreated();
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? ((err.data as { error?: string; message?: string } | undefined)?.error ??
            (err.data as { message?: string } | undefined)?.message ??
            err.message)
          : null;
      toast.error(msg || 'Falha ao adicionar banner.');
    }
  });

  const steps: WizardStep[] = [
    {
      id: 'image',
      title: 'Imagem',
      icon: ImageIcon,
      validate: () => {
        if (!croppedFile) {
          toast.error('Selecione e recorte a imagem do banner.');
          return false;
        }
        return true;
      },
      content: (
        <div className="space-y-4">
          {!sourceSrc && !preview ? (
            <>
              <p className="text-sm text-muted-foreground">
                Selecione uma imagem e recorte na proporção 850×1272 (cartão).
              </p>
              <label
                htmlFor="bannerImage"
                className="group flex cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border bg-[hsl(var(--glass-bg)/0.4)] py-12 text-center transition-[border-color,background-color,box-shadow] duration-300 ease-out hover:border-primary/45 hover:bg-primary/[0.06]"
              >
                <ImagePlus className="size-8 text-muted-foreground transition-all duration-300 group-hover:scale-105 group-hover:text-primary" />
                <span className="text-xs text-muted-foreground">PNG ou JPG</span>
              </label>
              <Input
                id="bannerImage"
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (sourceSrc?.startsWith('blob:')) URL.revokeObjectURL(sourceSrc);
                  setSourceSrc(URL.createObjectURL(file));
                  setCroppedFile(null);
                  if (preview?.startsWith('blob:')) URL.revokeObjectURL(preview);
                  setPreview(null);
                }}
              />
            </>
          ) : null}

          {sourceSrc && !croppedFile ? (
            <BannerImageCropper
              imageSrc={sourceSrc}
              onCropped={(file, url) => {
                setCroppedFile(file);
                setPreview(url);
                if (sourceSrc.startsWith('blob:')) URL.revokeObjectURL(sourceSrc);
                setSourceSrc(null);
              }}
              onCancel={() => {
                if (sourceSrc.startsWith('blob:')) URL.revokeObjectURL(sourceSrc);
                setSourceSrc(null);
              }}
            />
          ) : null}

          {croppedFile && preview ? (
            <div className="space-y-3">
              <div className="glass overflow-hidden rounded-xl">
                <img src={preview} alt="Prévia recortada" className="mx-auto max-h-80 w-full object-contain" />
              </div>
              <label className="inline-flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-lg border border-border/70 bg-foreground/[0.04] px-4 py-2.5 text-sm font-medium text-foreground backdrop-blur-sm transition-colors hover:bg-foreground/[0.08]">
                Trocar imagem
                <Input
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (preview.startsWith('blob:')) URL.revokeObjectURL(preview);
                    setCroppedFile(null);
                    setPreview(null);
                    setSourceSrc(URL.createObjectURL(file));
                  }}
                />
              </label>
            </div>
          ) : null}
        </div>
      ),
    },
    {
      id: 'content',
      title: 'Conteúdo',
      icon: Type,
      validate: () => form.trigger(['title', 'description', 'url']),
      content: (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="bannerTitle">Título</Label>
            <Input id="bannerTitle" placeholder="Promoção de inverno" {...form.register('title')} />
            {errors.title ? <p className="text-xs text-destructive">{errors.title.message}</p> : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="bannerDescription">Descrição</Label>
            <Textarea id="bannerDescription" rows={3} placeholder="Detalhe a oferta…" {...form.register('description')} />
            {errors.description ? (
              <p className="text-xs text-destructive">{errors.description.message}</p>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="bannerUrl">URL de destino (link)</Label>
            <Input id="bannerUrl" placeholder="https://…" {...form.register('url')} />
            {errors.url ? <p className="text-xs text-destructive">{errors.url.message}</p> : null}
          </div>
        </div>
      ),
    },
    {
      id: 'schedule',
      title: 'Agendamento',
      icon: CalendarClock,
      validate: () => form.trigger(['entryDate', 'exitDate']),
      content: (
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-xl border border-border bg-[hsl(var(--glass-bg)/0.4)] p-4">
            <div>
              <p className="text-sm font-medium">Expirar banner automaticamente</p>
              <p className="text-xs text-muted-foreground">Defina um período de exibição.</p>
            </div>
            <Switch checked={expiry} onCheckedChange={(v) => form.setValue('expiry', v)} />
          </div>
          {expiry ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-2">
                <Label htmlFor="entryDate">Data de entrada</Label>
                <Controller
                  name="entryDate"
                  control={form.control}
                  render={({ field }) => (
                    <DatePicker
                      id="entryDate"
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Selecione a entrada"
                      fromDate={today}
                      className="w-full"
                    />
                  )}
                />
                {errors.entryDate ? (
                  <p className="text-xs text-destructive">{errors.entryDate.message}</p>
                ) : null}
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="exitDate">Data de saída</Label>
                <Controller
                  name="exitDate"
                  control={form.control}
                  render={({ field }) => (
                    <DatePicker
                      id="exitDate"
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Selecione a saída"
                      className="w-full"
                      fromDate={(() => {
                        const entry = form.watch('entryDate');
                        if (!entry) return today;
                        const d = parseISO(entry);
                        return isValid(d) ? d : today;
                      })()}
                    />
                  )}
                />
                {errors.exitDate ? <p className="text-xs text-destructive">{errors.exitDate.message}</p> : null}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              O banner ficará ativo até ser removido manualmente.
            </p>
          )}
        </div>
      ),
    },
  ];

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-4xl overflow-x-hidden">
        <DialogHeader>
          <DialogTitle>Adicionar banner</DialogTitle>
          <DialogDescription>Imagem, conteúdo e agendamento.</DialogDescription>
        </DialogHeader>
        <Wizard
          steps={steps}
          onFinish={onValid}
          onCancel={() => onOpenChange(false)}
          isSubmitting={form.formState.isSubmitting}
          finishLabel="Adicionar banner"
          mobileOptimized
        />
      </DialogContent>
    </Dialog>
  );
}
